import { PayloadAction, createSlice } from '@reduxjs/toolkit';


import type Api from '~/types';

import { isInIframe, searchToObject } from '~/utils';
import { defaultAccount } from '~/store/appDefaultData';
import {
    closeStartBuyDialog,
    closeMultipleBuyDialog,
    closeBoosterDialog,
} from '~/store/dialogs';


const queryParams = searchToObject();
let noAnimation = true;
if (queryParams.noAnimation === false) {
    noAnimation = false;
}

const isInGame = window.navigator.userAgent.includes('WOWS/1');


export interface State {
    isBlurred: boolean,
    blueprintsAnimationDurations: Array<number>,
    blueprintsSvgData: BlueprintsSvgData,
    blueprintsCount: number,
    isInIframe: boolean,
    showThirdCard: boolean,
    secondaryShipLevels: Array<Api.IExtraShipLevel>,
    tertiaryShipLevels: Array<Api.IExtraShipLevel>,
    isRepeating: boolean,
    isLaunchingPassed: boolean,
    targetLevel: number | null,
    isEndReached: boolean,
    isInitialLoad: boolean,
    noAnimation: boolean,
    appIsReady: boolean,
    browserIsReady: boolean,
    guideIsRunning: boolean,
    isInGame: boolean,
    boosters: Array<Api.IBooster>,
    rewards: Array<Api.IReward>,
    specialRewards: Array<Api.IReward>,
    upgradeBoosterId: string,
    selectedBooster: Api.IBooster | null,
    shipLevel: number,
    progressShipLevel: number,
    animationShipLevel: number,
    animationShipLevelIsInstant: boolean,
    account: Api.IAccount,
    availableRewards: Array<string>,
    shipLevels: Array<Api.ShipLevel>,
    pendingBoostersIds: Array<string>,
    isSyncEnabled: boolean,
    launchingLevelId: number,
    videoType: VideoType | null,
    isIntroWatched: boolean,
    actions: Array<Api.Action>,
    animationStateSubtitle: string,
    isWaitingRepeat: boolean,
    progressFinishTime: number,
    progressStartTime: number,
    progressFinishTimeDefault: number,
    progressStartTimeDefault: number,
    // currencies: Array<Api.Currency>
    currency: string,
    volume: number,
    isEventFinished: boolean,
    isRepeatSidebarVisible: boolean,
    isAnimationInProgress: boolean,
    predictProgress: number,
    lastLevel: number,
    actionLevels: ActionLevelMap,
    closeTime: number,
    isPendingIssueRewards: boolean,
    isPendingGetData: boolean,
    isPendingCompleteAction: boolean,
    isPendingBuyBooster: boolean,
    isPendingSetShipLevel: boolean,
    isWorldShow: boolean,
    mainRewardShipId: string,
    mainRewardShipName: string,
    isSteambotBackButtonVisible: boolean,
    isPauseAnimation: boolean,
    nextStep: boolean,
    previousStep: boolean,
    isStartBoostersBought: boolean,
    congratulationBackground: string,
    shipPreviewBackground: string | null,
    secondaryShipPreviewBackground: string | null,
    educationBackground: string,
    kitBackgrounds: string[],
    blueprintsBackgrounds: Array<Api.BlueprintsBackground> | [],
    blueprintArts: string[],
    plugOnWeb: string,
    purchaseConfirmationBackground: string,
    banner: Api.Banner | null,
    readMoreLink: string,
    rules: Array<Api.Rule>,
    compensations: Array<CompensationItem>,
    educationSteps: Array<Api.EducationStep>,
    levelsCompletedBySpendingDoubloons: number,
    freeLevels: number,
    starterPacksMaxLevel: number,
    timings: Array<number>,
    missionsFinishVersion: string,
    eventFinishVersion: string,
    showDevPanel: boolean,
    showResetButton: boolean,
    boosterId: string,
}

const initialState: State = {
    isBlurred: false,
    blueprintsAnimationDurations: [],
    blueprintsSvgData: {},
    blueprintsCount: 0,
    isInIframe: isInIframe(),
    showThirdCard: true,
    secondaryShipLevels: [],
    tertiaryShipLevels: [],
    isRepeating: false,
    isLaunchingPassed: false,
    targetLevel: null,
    isEndReached: false,
    isInitialLoad: true,
    noAnimation,
    appIsReady: false,
    browserIsReady: false,
    guideIsRunning: false,
    isInGame,
    boosters: [],
    rewards: [],
    specialRewards: [],
    upgradeBoosterId: '',
    selectedBooster: null,
    shipLevel: 0,
    progressShipLevel: 0,
    animationShipLevel: 0,
    animationShipLevelIsInstant: true,
    account: defaultAccount,
    availableRewards: [],
    shipLevels: [],
    pendingBoostersIds: [],
    isSyncEnabled: true,
    launchingLevelId: 99,
    videoType: null,
    isIntroWatched: false,
    actions: [],
    animationStateSubtitle: '',
    isWaitingRepeat: false,
    progressFinishTime: 0,
    progressStartTime: 0,
    progressFinishTimeDefault: 0,
    progressStartTimeDefault: 0,
    currency: '',
    volume: 0,
    isEventFinished: false,
    isRepeatSidebarVisible: false,
    isAnimationInProgress: false,
    isWorldShow: true,
    predictProgress: 0,
    lastLevel: 0,
    actionLevels: {
        education: 0,
        introVideo: 0,
        launchingVideo: 0,
        finalVideo: 0,
        completeBlueprints: 0,
    },
    closeTime: 0,
    isPendingIssueRewards: false,
    isPendingGetData: false,
    isPendingCompleteAction: false,
    isPendingBuyBooster: false,
    isPendingSetShipLevel: false,
    mainRewardShipId: '',
    mainRewardShipName: '',
    isSteambotBackButtonVisible: false,
    isPauseAnimation: false,
    nextStep: false,
    previousStep: false,
    isStartBoostersBought: false,
    congratulationBackground: '',
    shipPreviewBackground: '',
    secondaryShipPreviewBackground: '',
    educationBackground: '',
    kitBackgrounds: [],
    blueprintsBackgrounds: [],
    blueprintArts: [],
    plugOnWeb: '',
    purchaseConfirmationBackground: '',
    banner: null,
    readMoreLink: '',
    rules: [],
    compensations: [],
    educationSteps: [],
    levelsCompletedBySpendingDoubloons: 0,
    freeLevels: 0,
    starterPacksMaxLevel: 0,
    timings: [],
    missionsFinishVersion: '',
    eventFinishVersion: '',
    showDevPanel: false,
    showResetButton: false,
    boosterId: '',
};

const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        getData(state: State, action: PayloadAction<{
            showThirdCard: boolean,
            mainRewardShipId: string,
            mainRewardShipName: string,
            secondaryShipLevels: Array<Api.IExtraShipLevel>,
            tertiaryShipLevels: Array<Api.IExtraShipLevel>,
            boosters: Array<Api.IBooster>,
            rewards: Array<Api.IReward>,
            specialRewards: Array<Api.IReward>,
            shipLevels: Array<Api.ShipLevel>,
            launchingLevelId: number,
            actions: Array<Api.Action>,
            isIntroWatched: boolean,
            progressFinishTime: number,
            progressStartTime: number,
            progressFinishTimeDefault: number,
            progressStartTimeDefault: number,
            // currencies: Array<Api.Currency>,
            currency: string,
            animationShipLevel: number,
            actionLevels: ActionLevelMap,
            closeTime: number,
            blueprintsCount: number,
            congratulationBackground: string,
            shipPreviewBackground: string | null,
            secondaryShipPreviewBackground: string | null,
            educationBackground: string,
            kitBackgrounds: string[],
            blueprintsBackgrounds: Array<Api.BlueprintsBackground>,
            blueprintArts: string[],
            plugOnWeb: string,
            purchaseConfirmationBackground: string,
            banner: Api.Banner | null,
            readMoreLink: string,
            rules: Array<Api.Rule>,
            compensations: Array<CompensationItem>,
            educationSteps: Array<Api.EducationStep>,
            levelsCompletedBySpendingDoubloons: number,
            freeLevels: number,
            lastLevel: number,
            starterPacksMaxLevel: number,
            timings: Array<number>,
            missionsFinishVersion: string,
            eventFinishVersion: string,
            showDevPanel: boolean,
            showResetButton: boolean,
        }>) {
            Object.assign(state, action.payload);
        },

        setAppIsReady(state: State) {
            state.appIsReady = true;
        },

        setBrowserIsReady(state: State, action: PayloadAction<{
            volume: number,
        }>) {
            state.browserIsReady = true;
            state.volume = action.payload.volume;
        },

        setTargetLevel(state: State, action: PayloadAction<{
            level: number | null,
        }>) {
            state.targetLevel = action.payload.level;
        },

        setProgressShipLevel(state: State, action: PayloadAction<{
            level: number,
        }>) {
            state.progressShipLevel = action.payload.level;
        },

        updateAccount(state: State, action: PayloadAction<{
            account: Api.IAccount,
            isEndReached: boolean,
            isEventFinished: boolean,
            lastLevel: number,
            isStartBoostersBought: boolean,
        }>) {
            const newState = {
                account: action.payload.account,
                isInitialLoad: false,
                isEndReached: action.payload.isEndReached,
                isEventFinished: action.payload.isEventFinished,
                lastLevel: action.payload.lastLevel,
                isStartBoostersBought: action.payload.isStartBoostersBought,
            };
            Object.assign(state, newState);
        },

        switchSync(state: State, action: PayloadAction<boolean | undefined>) {
            state.isSyncEnabled = action.payload ?? !state.isSyncEnabled;
        },

        updateAvailableRewards(state: State, action: PayloadAction<{ availableRewardsIds: Array<string> }>) {
            state.availableRewards = action.payload.availableRewardsIds;
        },

        showIntroVideo(state: State) {
            state.videoType = 'intro';
        },
        showLaunchVideo(state: State) {
            state.videoType = 'launch';
        },
        showCompleteBlueprintsVideo(state: State) {
            state.videoType = 'completeBlueprints';
        },
        showFinalVideo(state: State) {
            state.videoType = 'final';
        },

        setIssueRewardsPendingState(state: State, action: PayloadAction<boolean>) {
            state.isPendingIssueRewards = action.payload;
        },

        setGetDataPendingState(state: State, action: PayloadAction<boolean>) {
            state.isPendingGetData = action.payload;
        },

        setWorldRendererState(state: State, action: PayloadAction<boolean>) {
            state.isWorldShow = action.payload;
        },

        turnGuideOn(state: State) {
            state.guideIsRunning = true;
        },
        turnGuideOff(state: State) {
            state.guideIsRunning = false;
        },

        enableBlur(state: State) {
            state.isBlurred = true;
        },
        disableBlur(state: State) {
            state.isBlurred = false;
        },

        setPreviousStep(state: State, action: PayloadAction<boolean>) {
            state.previousStep = action.payload;
        },

        setNextStep(state: State, action: PayloadAction<boolean>) {
            state.nextStep = action.payload;
        },

        setTimings(state: State, action: PayloadAction<Array<number>>) {
            state.timings = action.payload;
        },

        setPauseAnimation(state: State, action: PayloadAction<boolean>) {
            state.isPauseAnimation = action.payload;
        },

        getBlueprintsSvgData(state: State, action: PayloadAction<{
            data: BlueprintsSvgData,
            durations: Array<number>,
        }>) {
            state.blueprintsSvgData = action.payload.data;
            state.blueprintsAnimationDurations = action.payload.durations;
        },

        updateBackButtonVisibility(state: State, action: PayloadAction<string>) {
            state.isSteambotBackButtonVisible = action.payload !== '/';
        },

        setSetShipLevelPendingState(state: State, action: PayloadAction<boolean>) {
            state.isPendingSetShipLevel = action.payload;
        },

        setBuyBoosterPendingState(state: State, action: PayloadAction<boolean>) {
            state.isPendingBuyBooster = action.payload;
        },

        setCompleteActionPendingState(state: State, action: PayloadAction<boolean>) {
            state.isPendingCompleteAction = action.payload;
        },

        setAnimationState(state: State, action: PayloadAction<boolean>) {
            state.isAnimationInProgress = action.payload;
        },

        showRepeatSidebar(state: State) {
            state.isRepeatSidebarVisible = true;
        },
        hideRepeatSidebar(state: State) {
            state.isRepeatSidebarVisible = false;
        },

        setRepeatingState(state: State, action: PayloadAction<boolean>) {
            state.isRepeating = action.payload;
        },

        updateAnimationLevel(state: State, action: PayloadAction<number>) {
            state.animationShipLevel = action.payload;
        },

        updateAnimationSubtitle(state: State, action: PayloadAction<{
            animationStateSubtitle: string,
            level: number,
            isInstant: boolean,
        }>) {
            state.animationStateSubtitle = action.payload.animationStateSubtitle;
            state.animationShipLevel = action.payload.level;
            state.animationShipLevelIsInstant = action.payload.isInstant;
        },

        clearBoosterId(state: State) {
            state.upgradeBoosterId = '';
        },

        setSelectedBooster(state: State, action: PayloadAction<Api.IBooster>) {
            state.selectedBooster = action.payload;
            state.boosterId = action.payload?.id || '';
        },

        updateClientState(state: State, action: PayloadAction<number>) {
            state.shipLevel = action.payload;
        },

        setShipLevel(state: State, action: PayloadAction<number>) {
            state.shipLevel = action.payload;
        },

        setPredictProgress(state: State, action: PayloadAction<number>) {
            state.predictProgress = action.payload;
        },

        setDefaultDates(state: State) {
            state.progressStartTime = state.progressStartTimeDefault;
            state.progressFinishTime = state.progressFinishTimeDefault;
        },
        setEventStartDate(state: State, action: PayloadAction<number>) {
            state.progressStartTime = action.payload;
        },
        setEventEndDate(state: State, action: PayloadAction<number>) {
            state.progressFinishTime = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(closeStartBuyDialog, (state: State) => {
                state.predictProgress = 0;
            })
            .addCase(closeMultipleBuyDialog, (state: State) => {
                state.predictProgress = 0;
            })
            .addCase(closeBoosterDialog, (state: State) => {
                state.selectedBooster = null;
                state.boosterId = '';
            });
    },
});

export default appSlice.reducer;


// All reducers for actions/middleware
export const appReducerActions = appSlice.actions;
